import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoResizeTextarea, Button, Input, Spinner } from '../common';
import { useSceneTools } from '../../hooks/useSceneTools';
import type { TaggedSuggestion } from '../writing/ClaimSuggestionPanel';
import type { FactFinding } from '../../types';

type Tab = 'prose' | 'moments' | 'interview' | 'style' | 'facts';

interface Props {
  sceneId: string;
  bookId: string;
  sceneTitle?: string;
  // A phrase the reader selected — opens on the interview tab to "talk about it".
  seedText?: string | null;
  onClose: () => void;
  onOpenFullEditor: () => void;
  // Called after a change that affects the prose, so the reader refetches.
  onChanged?: () => void;
}

export default function SceneToolsSheet({
  sceneId,
  bookId,
  sceneTitle,
  seedText,
  onClose,
  onOpenFullEditor,
  onChanged,
}: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const tools = useSceneTools(sceneId, bookId, { onError: setError, onNotice: setNotice });
  const { scene } = tools;

  const [tab, setTab] = useState<Tab>(seedText ? 'interview' : 'prose');
  // Open unwritten scenes on the plan tab (the plan is the spine you build first);
  // written scenes open on prose. Decided once, on first scene load.
  const [tabPicked, setTabPicked] = useState(false);

  // Drafts / busy state (kept local; the hook owns the network calls).
  const [bodyDraft, setBodyDraft] = useState('');
  const [newMoment, setNewMoment] = useState('');
  const [momentDrafts, setMomentDrafts] = useState<Record<string, string>>({});
  // Seed the interview reply with the selected phrase ("talk about this").
  const [reply, setReply] = useState(() =>
    seedText ? `${t('bookReader.talkAboutPrefix', 'About this passage')}: “${seedText}” — ` : ''
  );
  const [styleDraft, setStyleDraft] = useState('');
  const [styleGuidance, setStyleGuidance] = useState('');
  const [suggestions, setSuggestions] = useState<TaggedSuggestion[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  // Ephemeral fact-check findings — null until the author runs a check.
  const [findings, setFindings] = useState<FactFinding[] | null>(null);

  // Reseed the editable drafts from canonical scene data whenever the scene
  // (re)loads — adjust-state-during-render keyed on updatedAt, which the server
  // bumps on every mutation. (See react.dev "you might not need an effect".)
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const sceneStamp = scene ? `${scene.id}:${scene.updatedAt ?? ''}` : null;
  if (scene && sceneStamp !== syncedAt) {
    setSyncedAt(sceneStamp);
    setBodyDraft(scene.body ?? '');
    setStyleDraft(scene.storyBiblePlotBeat?.draftBlock?.writingStyle ?? '');
    const map: Record<string, string> = {};
    (scene.planPoints ?? []).forEach((m) => {
      map[m.id] = m.content;
    });
    setMomentDrafts(map);
  }

  if (scene && !tabPicked) {
    setTabPicked(true);
    if (!seedText && !(scene.body && scene.body.trim())) setTab('moments');
  }

  const styleSuggestions = scene?.storyBiblePlotBeat?.draftBlock?.writingStyleSuggestions ?? [];
  const scenePlan = scene?.planPoints ?? [];
  const activeInterview = tools.activeInterview;

  // Wrap a hook action with busy state + parent notification.
  const withBusy = async (id: string, fn: () => Promise<boolean>, affectsProse = false) => {
    setBusy(id);
    setError(null);
    setNotice(null);
    const ok = await fn();
    setBusy(null);
    if (ok && affectsProse) onChanged?.();
    return ok;
  };

  const wordCount = useMemo(() => {
    const txt = (scene?.body ?? '').trim();
    return txt ? txt.split(/\s+/).length : 0;
  }, [scene]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'prose', label: t('bookReader.tabProse', 'Prose') },
    { key: 'moments', label: t('bookReader.tabScenePlan', 'Key events') },
    { key: 'interview', label: t('bookReader.tabInterview', 'Interview') },
    { key: 'style', label: t('bookReader.tabStyle', 'Style') },
    { key: 'facts', label: t('bookReader.tabFacts', 'Facts') },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true">
      <button
        aria-label={t('common.close', 'Close')}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30"
      />
      <div className="relative flex max-h-[85vh] flex-col rounded-t-2xl bg-white shadow-2xl dark:bg-gray-900">
        <div className="mx-auto mt-3 h-1 w-10 shrink-0 rounded-full bg-slate-300" />
        <div className="flex shrink-0 items-start justify-between gap-2 px-5 pb-2 pt-3">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
              {sceneTitle || t('bookReader.sceneTools', 'Scene tools')}
            </h2>
            <p className="text-xs text-slate-400">
              {t('bookReader.sceneToolsSubtitle', 'Shape this scene')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="shrink-0 rounded-md px-2 py-1 text-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-gray-800"
          >
            ✕
          </button>
        </div>
        <div className="overflow-y-auto px-5 pb-6">
          {!scene ? (
            <div className="grid place-items-center py-12">
              <Spinner />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Tabs */}
              <div className="flex flex-wrap gap-1 border-b border-gray-200 pb-2 dark:border-gray-700">
                {tabs.map((tb) => (
                  <button
                    key={tb.key}
                    type="button"
                    onClick={() => setTab(tb.key)}
                    className={`rounded-md px-3 py-1.5 text-sm ${
                      tab === tb.key
                        ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`}
                  >
                    {tb.label}
                    {tb.key === 'moments' && scenePlan.length > 0 ? ` (${scenePlan.length})` : ''}
                  </button>
                ))}
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  {error}
                </div>
              )}
              {notice && (
                <div className="rounded-md bg-emerald-50 p-2 text-sm text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
                  {notice}
                </div>
              )}

              {/* ---- PROSE ---- */}
              {tab === 'prose' && (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span>
                      {wordCount} {t('chaptersFirst.words', 'words')}
                    </span>
                    {scene.health && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 dark:bg-gray-800">
                        {scene.health}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      isLoading={busy === 'preview'}
                      disabled={!!busy}
                      onClick={() => withBusy('preview', () => tools.generatePreview(true), true)}
                    >
                      ↻ {t('bookReader.quickRedraft', 'Quick redraft')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      isLoading={busy === 'expand'}
                      disabled={!!busy || !tools.draftBlockId}
                      onClick={() => withBusy('expand', () => tools.expandBody(), true)}
                    >
                      ↻ {t('bookReader.fullExpand', 'Full re-expand')}
                    </Button>
                  </div>
                  <AutoResizeTextarea
                    minRows={8}
                    maxRows={30}
                    value={bodyDraft}
                    onChange={(e) => setBodyDraft(e.target.value)}
                    placeholder={t(
                      'sceneEditor.bodyPlaceholder',
                      'The actual scene — paragraphs, dialogue, sensory detail.'
                    )}
                  />
                  <div className="flex justify-between gap-2">
                    <Button size="sm" variant="outline" onClick={onOpenFullEditor}>
                      {t('bookReader.openFullEditor', 'Open full editor')}
                    </Button>
                    <Button
                      size="sm"
                      isLoading={busy === 'saveBody'}
                      disabled={!!busy || bodyDraft === (scene.body ?? '')}
                      onClick={() => withBusy('saveBody', () => tools.saveBody(bodyDraft), true)}
                    >
                      {t('common.save', 'Save')}
                    </Button>
                  </div>
                </div>
              )}

              {/* ---- KEY MOMENTS ---- */}
              {tab === 'moments' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t(
                      'sceneEditor.scenePlanHint',
                      'Beats this scene must hit — angles, moments, callbacks, sensory anchors.'
                    )}
                  </p>
                  {scenePlan.map((m) => (
                    <div
                      key={m.id}
                      className="space-y-2 rounded-md border border-gray-200 p-2 dark:border-gray-700"
                    >
                      <AutoResizeTextarea
                        minRows={2}
                        value={momentDrafts[m.id] ?? ''}
                        onChange={(e) => setMomentDrafts((p) => ({ ...p, [m.id]: e.target.value }))}
                      />
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          isLoading={busy === `lock-${m.id}`}
                          disabled={!!busy}
                          onClick={() =>
                            withBusy(`lock-${m.id}`, () =>
                              tools.togglePlanPointLock(m.id, m.locked)
                            )
                          }
                        >
                          {m.locked
                            ? t('sceneEditor.unlockPlanPoint', 'Unlock')
                            : t('sceneEditor.lockPlanPoint', 'Lock')}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          isLoading={busy === `del-${m.id}`}
                          disabled={!!busy}
                          onClick={() => withBusy(`del-${m.id}`, () => tools.deletePlanPoint(m.id))}
                        >
                          {t('common.delete', 'Delete')}
                        </Button>
                        <Button
                          size="sm"
                          isLoading={busy === `save-${m.id}`}
                          disabled={!!busy}
                          onClick={() =>
                            withBusy(`save-${m.id}`, () =>
                              tools.savePlanPoint(m.id, momentDrafts[m.id] ?? '')
                            )
                          }
                        >
                          {t('common.save', 'Save')}
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="space-y-2 rounded-md border border-dashed border-gray-300 p-2 dark:border-gray-700">
                    <AutoResizeTextarea
                      minRows={2}
                      value={newMoment}
                      onChange={(e) => setNewMoment(e.target.value)}
                      placeholder={t(
                        'sceneEditor.planPointPlaceholder',
                        'Example: Reveal the secret in the second-to-last beat.'
                      )}
                    />
                    <div className="flex justify-between gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        isLoading={busy === 'suggest'}
                        disabled={!!busy}
                        onClick={() =>
                          withBusy('suggest', async () => {
                            setSuggestions(
                              await tools.suggestScenePlan({
                                mode: wordCount < 200 ? 'fresh' : 'continue',
                              })
                            );
                            return true;
                          })
                        }
                      >
                        ✨ {t('sceneEditor.scenePlanSuggest', 'Suggest')}
                      </Button>
                      <Button
                        size="sm"
                        isLoading={busy === 'addMoment'}
                        disabled={!!busy || !newMoment.trim()}
                        onClick={() =>
                          withBusy('addMoment', async () => {
                            const ok = await tools.addPlanPoint(newMoment);
                            if (ok) setNewMoment('');
                            return ok;
                          })
                        }
                      >
                        {t('sceneEditor.addPlanPoint', 'Add key moment')}
                      </Button>
                    </div>
                  </div>
                  {suggestions.length > 0 && (
                    <div className="space-y-2 rounded-md border border-indigo-200 p-2 dark:border-indigo-800">
                      <p className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                        {t('bookReader.suggestionsTitle', 'Suggestions')}
                      </p>
                      {suggestions.map((s, i) => (
                        <div key={`${s.content}-${i}`} className="flex items-start gap-2 text-sm">
                          <span className="flex-1">{s.content}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!!busy}
                            onClick={() =>
                              withBusy(`addSug-${i}`, async () => {
                                const ok = await tools.addPlanPoint(s.content);
                                if (ok) setSuggestions((prev) => prev.filter((_, j) => j !== i));
                                return ok;
                              })
                            }
                          >
                            {t('sceneEditor.saveAsPlanPoint', 'Save')}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ---- INTERVIEW ---- */}
              {tab === 'interview' && (
                <div className="space-y-3">
                  {scenePlan.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {scenePlan.map((m, idx) => (
                        <button
                          key={m.id}
                          type="button"
                          disabled={!!busy}
                          onClick={() => withBusy(`angle-${m.id}`, () => tools.switchAngle(m.id))}
                          className="rounded-full border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:hover:bg-gray-800"
                          title={m.content}
                        >
                          A{idx + 1}: {m.content.slice(0, 28)}
                          {m.content.length > 28 ? '…' : ''}
                        </button>
                      ))}
                    </div>
                  )}
                  {!activeInterview ? (
                    <div className="flex justify-end">
                      <Button
                        isLoading={busy === 'startInterview'}
                        disabled={!!busy}
                        onClick={() => withBusy('startInterview', () => tools.startInterview())}
                      >
                        {t('sceneEditor.startInterview', 'Start interview')}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                        {activeInterview.messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`rounded-md border p-2 text-sm ${
                              msg.role === 'assistant'
                                ? 'border-blue-100 bg-blue-50 dark:border-blue-900 dark:bg-blue-900/20'
                                : msg.role === 'system'
                                  ? 'border-purple-100 bg-purple-50 dark:border-purple-900 dark:bg-purple-900/20'
                                  : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                            }`}
                          >
                            <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-500">
                              {msg.role === 'user'
                                ? t('sceneEditor.role.user', 'You')
                                : msg.role === 'assistant'
                                  ? t('sceneEditor.role.assistant', 'AI interviewer')
                                  : t('sceneEditor.role.system', 'System')}
                            </div>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                        ))}
                      </div>
                      {activeInterview.status !== 'completed' && (
                        <>
                          <AutoResizeTextarea
                            minRows={2}
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            placeholder={t(
                              'sceneEditor.interviewReplyPlaceholder',
                              'Answer with concrete details, sensory cues, specific intent.'
                            )}
                          />
                          <div className="flex justify-between gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              isLoading={busy === 'clearInterview'}
                              disabled={!!busy}
                              onClick={() =>
                                withBusy('clearInterview', () => tools.clearInterview())
                              }
                            >
                              {t('sceneEditor.clearInterview', 'Clear')}
                            </Button>
                            <Button
                              isLoading={busy === 'sendReply'}
                              disabled={!!busy || !reply.trim()}
                              onClick={() =>
                                withBusy('sendReply', async () => {
                                  const ok = await tools.sendInterviewMessage(reply);
                                  if (ok) setReply('');
                                  return ok;
                                })
                              }
                            >
                              {t('sceneEditor.sendReply', 'Send reply')}
                            </Button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ---- WRITING STYLE ---- */}
              {tab === 'style' && (
                <div className="space-y-3">
                  {!tools.draftBlockId ? (
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {t(
                        'sceneEditor.styleUnavailable',
                        'This scene is not yet linked to a draft block, so style override is unavailable.'
                      )}
                    </p>
                  ) : (
                    <>
                      <AutoResizeTextarea
                        minRows={3}
                        value={styleDraft}
                        onChange={(e) => setStyleDraft(e.target.value)}
                        placeholder={t(
                          'sceneEditor.stylePlaceholder',
                          'Voice, rhythm, tone for this scene.'
                        )}
                      />
                      <Input
                        label={t(
                          'sceneEditor.styleSuggestionLabel',
                          'AI style guidance (optional)'
                        )}
                        value={styleGuidance}
                        onChange={(e) => setStyleGuidance(e.target.value)}
                      />
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          isLoading={busy === 'genStyle'}
                          disabled={!!busy}
                          onClick={() =>
                            withBusy(
                              'genStyle',
                              async () => {
                                const ok = await tools.generateWritingStyle(styleGuidance);
                                if (ok) setStyleGuidance('');
                                return ok;
                              },
                              true
                            )
                          }
                        >
                          {t('sceneEditor.generateStyle', 'Generate with AI')}
                        </Button>
                        <Button
                          size="sm"
                          isLoading={busy === 'saveStyle'}
                          disabled={!!busy || !styleDraft.trim()}
                          onClick={() =>
                            withBusy('saveStyle', () => tools.saveWritingStyle(styleDraft), true)
                          }
                        >
                          {t('sceneEditor.saveStyle', 'Save style')}
                        </Button>
                      </div>
                      {styleSuggestions.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {styleSuggestions.map((s, i) => (
                            <button
                              key={`${s}-${i}`}
                              type="button"
                              onClick={() => setStyleDraft(s)}
                              className="rounded-full border border-indigo-200 px-3 py-1.5 text-sm text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-900/30"
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ---- FACT CHECK ---- */}
              {tab === 'facts' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t(
                      'bookReader.factCheckHint',
                      "Check this scene's prose against your trusted facts. The AI only flags claims for your review — it never edits the text."
                    )}
                  </p>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      isLoading={busy === 'factCheck'}
                      disabled={!!busy || !(scene.body && scene.body.trim())}
                      onClick={() =>
                        withBusy('factCheck', async () => {
                          setFindings(await tools.checkFacts());
                          return true;
                        })
                      }
                    >
                      🔎 {t('bookReader.factCheckRun', 'Check facts')}
                    </Button>
                  </div>

                  {findings !== null && findings.length === 0 && (
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">
                      {t('bookReader.factCheckClean', 'Nothing flagged — no contradictions or unbacked claims found.')}
                    </p>
                  )}

                  {findings && findings.length > 0 && (
                    <ul className="space-y-2">
                      {findings.map((f, i) => (
                        <li
                          key={`${f.claim}-${i}`}
                          className="space-y-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 dark:border-amber-900 dark:bg-amber-900/20"
                        >
                          <div className="flex items-start gap-2">
                            <span
                              className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${
                                f.verdict === 'contradicts'
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                              }`}
                            >
                              {t(`bookReader.factVerdict.${f.verdict}`, f.verdict)}
                            </span>
                            <span className="flex-1 text-sm text-gray-800 dark:text-gray-100">{f.claim}</span>
                          </div>
                          {f.reason && <p className="text-xs text-gray-500">{f.reason}</p>}
                          {f.sentence && (
                            <p className="text-xs italic text-gray-400">“{f.sentence}”</p>
                          )}
                          <div className="flex justify-end gap-2">
                            {f.verdict === 'unsupported' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                isLoading={busy === `addFact-${i}`}
                                disabled={!!busy}
                                onClick={() =>
                                  withBusy(`addFact-${i}`, async () => {
                                    const ok = await tools.addFact(f.claim);
                                    if (ok) setFindings((prev) => (prev ?? []).filter((_, j) => j !== i));
                                    return ok;
                                  })
                                }
                              >
                                {t('bookReader.factAddToFacts', 'Add to facts')}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!!busy}
                              onClick={() => setFindings((prev) => (prev ?? []).filter((_, j) => j !== i))}
                            >
                              {t('bookReader.factDismiss', 'Dismiss')}
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
